/**
 * WI-14 (OPERATOR-UI-PLACEMENT): typed Desktop deep-link builder.
 *
 * The operator console is deliberately thin: it only READS execution state and
 * hands the operator off to the native Aldan Desktop console for anything
 * actionable (opening a run group, inspecting an attempt, resolving a
 * reconciliation). It never embeds or reimplements the native console, so every
 * "open in Desktop" action is a typed deep link built here — a single, audited
 * surface for the URL contract rather than string-concatenation at call sites.
 *
 * Unknown: pending design — the concrete deep-link URL scheme (and the Desktop
 * manifest/capability contract it must satisfy) is owned by aldan-desktop and is
 * not finalized. `DESKTOP_DEEP_LINK_SCHEME` is a structural placeholder; do not
 * treat it as the committed production scheme.
 */
export const DESKTOP_DEEP_LINK_SCHEME = 'aldan-desktop';

/** The typed set of Desktop targets the thin console can hand off to. */
export type DesktopDeepLinkTarget =
  | { kind: 'run-group'; runGroupId: string }
  | { kind: 'attempt'; instanceId: string }
  | { kind: 'worker'; workerId: string }
  | { kind: 'evidence'; instanceId: string }
  | { kind: 'reconciliation'; instanceId: string };

/**
 * Build a typed Desktop deep link for a target. Path segments are
 * percent-encoded so ids are always transported safely.
 */
export function buildDesktopDeepLink(target: DesktopDeepLinkTarget): string {
  const base = `${DESKTOP_DEEP_LINK_SCHEME}://`;
  const enc = encodeURIComponent;
  switch (target.kind) {
    case 'run-group':
      return `${base}run-groups/${enc(target.runGroupId)}`;
    case 'attempt':
      return `${base}attempts/${enc(target.instanceId)}`;
    case 'worker':
      return `${base}workers/${enc(target.workerId)}`;
    case 'evidence':
      return `${base}attempts/${enc(target.instanceId)}/evidence`;
    case 'reconciliation':
      return `${base}attempts/${enc(target.instanceId)}/reconciliation`;
    default: {
      // Exhaustiveness guard: a new target kind must be handled explicitly.
      const _never: never = target;
      return _never;
    }
  }
}

/** Convenience helpers mirroring the console's read surfaces. */
export const desktopDeepLinks = {
  runGroup: (runGroupId: string) => buildDesktopDeepLink({ kind: 'run-group', runGroupId }),
  attempt: (instanceId: string) => buildDesktopDeepLink({ kind: 'attempt', instanceId }),
  worker: (workerId: string) => buildDesktopDeepLink({ kind: 'worker', workerId }),
  evidence: (instanceId: string) => buildDesktopDeepLink({ kind: 'evidence', instanceId }),
  reconciliation: (instanceId: string) =>
    buildDesktopDeepLink({ kind: 'reconciliation', instanceId }),
};
